import {
  Condition,
  ConditionType,
  DepositPayable,
  DepositType,
  ValueType,
  type Deposit,
} from '@urbanx/agx.campaigns.client';
import { dateOnlyToDate, type DateOnlyInput } from '@urbanx/agx.ui-components2';

type DepositAmountFields = {
  amount?: number | null;
  percent?: number | null;
};

const roundCurrencyAmount = (value?: number | null) => Math.round((value ?? 0) * 100) / 100;

export const getEarlyReleaseAmountError = (value: string, availableAmount: number) => {
  const normalizedValue = value.replaceAll(',', '').trim();

  if (!normalizedValue) return 'Amount is required';
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalizedValue)) {
    const isDecimalPrecision = /^\d+(?:\.\d+)?$/.test(normalizedValue);
    return isDecimalPrecision
      ? 'Enter an amount with no more than 2 decimal places'
      : 'Enter a valid amount';
  }

  const amount = Number(normalizedValue);
  if (amount <= 0) return 'Amount must be greater than $0';
  if (amount > availableAmount) {
    return `Amount cannot exceed ${availableAmount.toLocaleString('en-AU', {
      style: 'currency',
      currency: 'AUD',
    })}`;
  }

  return undefined;
};

export const getAmountFromDeposit = (deposit?: DepositAmountFields, salePrice?: number) => {
  if (deposit?.amount != null) return roundCurrencyAmount(deposit.amount);
  if (deposit?.percent != null && salePrice != null) {
    return roundCurrencyAmount(salePrice * (deposit.percent / 100));
  }

  return 0;
};

export const getTotalDepositAmount = ({
  initialDeposit,
  balanceDeposit,
  salePrice,
}: {
  initialDeposit?: DepositAmountFields;
  balanceDeposit?: DepositAmountFields;
  salePrice?: number;
}) =>
  getAmountFromDeposit(initialDeposit, salePrice) + getAmountFromDeposit(balanceDeposit, salePrice);

export const getDepositExcessAmount = ({
  totalDepositAmount,
  salePrice,
}: {
  totalDepositAmount: number;
  salePrice?: number | null;
}) => {
  if (salePrice == null) return undefined;

  const tenPercentOfSalePrice = salePrice / 10;
  return roundCurrencyAmount(totalDepositAmount - tenPercentOfSalePrice);
};

export const getNswDepositSplit = ({
  totalDepositAmount,
  salePrice,
  fullDepositUpfront,
}: {
  totalDepositAmount: number;
  salePrice: number;
  fullDepositUpfront: boolean;
}) => {
  if (fullDepositUpfront) {
    return {
      initialDepositAmount: totalDepositAmount,
      balanceDepositAmount: undefined,
    };
  }

  // NSW split deposits collect 0.25% upfront and defer the remaining balance.
  const initialDepositAmount = getAmountFromDeposit({ percent: 0.25 }, salePrice);
  const balanceDepositAmount = roundCurrencyAmount(totalDepositAmount - initialDepositAmount);

  return {
    initialDepositAmount,
    balanceDepositAmount: balanceDepositAmount > 0 ? balanceDepositAmount : undefined,
  };
};

export const getNswBalanceDepositDueDate = (expectedUnconditionalDate?: DateOnlyInput | null) =>
  dateOnlyToDate(expectedUnconditionalDate);

export const getNswBalanceDepositWithDueDate = ({
  deposit,
  dueDateString,
}: {
  deposit?: Deposit;
  dueDateString?: string;
}) => {
  if (!deposit || deposit.depositType !== DepositType.Balance || !dueDateString) {
    return deposit;
  }

  return {
    ...deposit,
    condition: Condition.OnDate,
    conditionType: ConditionType.Unconditional,
    referenceDate: dueDateString,
  };
};

export const normalizeDepositToDollar = (
  deposit: Deposit | undefined,
  salePrice?: number
): Deposit | undefined => {
  if (!deposit) return undefined;

  if (
    deposit.amount != null &&
    deposit.depositValueType === ValueType.Dollar &&
    deposit.percent == null
  ) {
    return deposit;
  }

  const amount =
    deposit.amount != null
      ? deposit.amount
      : deposit.percent != null && salePrice != null
        ? getAmountFromDeposit(deposit, salePrice)
        : undefined;

  // Keep percentage deposits intact until sale price is available to derive dollars.
  if (amount == null) return deposit;

  return {
    ...deposit,
    amount,
    percent: undefined,
    depositValueType: ValueType.Dollar,
  };
};

export const getDepositWithEditedCondition = ({
  deposit,
  updatedDeposit,
  isNsw,
  salePrice,
  amount,
  percent,
}: {
  deposit: Deposit;
  updatedDeposit: Deposit;
  isNsw: boolean;
  salePrice?: number;
  amount?: number;
  percent?: number;
}): Deposit => {
  const nextDeposit = {
    ...deposit,
    ...updatedDeposit,
    depositType: deposit.depositType,
  };
  const normalizedDeposit =
    nextDeposit.condition === Condition.OnSpecificDate
      ? {
          ...nextDeposit,
          depositPayable: DepositPayable.On,
          daysFromDate: undefined,
          dayType: undefined,
          fromDateTimeUnit: undefined,
          specialConditionId: undefined,
          other: undefined,
        }
      : nextDeposit;

  if (isNsw) {
    return normalizeDepositToDollar(normalizedDeposit, salePrice) ?? normalizedDeposit;
  }

  return {
    ...normalizedDeposit,
    amount: amount ?? normalizedDeposit.amount,
    percent: percent ?? normalizedDeposit.percent,
    depositValueType: deposit.depositValueType,
  };
};
